// App.js
import React, { useCallback, useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "./assets/scss/theme.scss";
import "./App.css";
import { Switch, BrowserRouter as Router } from "react-router-dom";

import { userRoutes, authRoutes } from "./routes/allRoutes";
import Authmiddleware from "./routes/middleware/Authmiddleware";
import Detailmiddleware from "./routes/middleware/Detailmiddleware";
import ResponsiveAppBar from "./pages/Sidebar/ResponsiveAppBar";
import LoginSidebar from "./pages/Sidebar/LoginSidebar";

import secureLocalStorage from "react-secure-storage";
import IdleTimerProvider from "./idle/IdleTimerProvider.jsx";
import ForceLogoutDialog from "./components/ForceLogoutDialog";
import { agmsClientSessionCheck } from "./apis/request";

const App = () => {
  // 현재 토큰 상태를 읽는 함수
  const readAuthed = useCallback(() => {
    try {
      return Boolean(secureLocalStorage.getItem("accesstoken"));
    } catch {
      return false;
    }
  }, []);

  // ✅ 로그인/로그아웃 변화에 반응하도록 state로 관리
  const [isAuthed, setIsAuthed] = useState(() => readAuthed());

  // ✅ 다른 탭(storage) + 동일 탭(custom event) 모두 반영
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "accesstoken" || e.key === "refreshtoken" || e.key === "authUser") {
        setIsAuthed(readAuthed());
      }
    };
    const onTokenUpdated = () => setIsAuthed(readAuthed());

    window.addEventListener("storage", onStorage);
    window.addEventListener("token-updated", onTokenUpdated);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("token-updated", onTokenUpdated);
    };
  }, [readAuthed]);

  // ✅ 다른 곳에서 로그인 감지(능동): 화면을 가만히 둬도 잡히도록
  // 인증 중일 때 10초마다 세션 체크 1건. refresh 토큰 활성 여부만 확인 → access 만료 안 기다리고 즉시 강제 로그아웃 모달.
  useEffect(() => {
    if (!isAuthed) return;
    const POLL_MS = 10 * 1000;
    const timer = setInterval(agmsClientSessionCheck, POLL_MS);
    return () => clearInterval(timer);
  }, [isAuthed]);

  const handleWarn = () => {
    console.warn("⚠️ 1분 후 자동 로그아웃됩니다.");
  };

  const handleTimeout = async () => {
    let is_accesstoken_exist = false;
    alert("세션 만료! 재로그인 해주세요.");
    try {
      if(secureLocalStorage.getItem("accesstoken") !== null){
        is_accesstoken_exist = true;
        secureLocalStorage.removeItem("accesstoken");
        secureLocalStorage.removeItem("refreshtoken");
        secureLocalStorage.removeItem("authUser");
      }
    } finally {
      // ✅ 동일 탭에서도 즉시 인증 상태 반영
      if(is_accesstoken_exist){
        window.dispatchEvent(new Event("token-updated"));
        window.location.replace("/login");
      }
    }
  };

  const AppRoutes = (
    <Switch>
      {authRoutes.map((route, idx) => (
        <Detailmiddleware
          path={route.path}
          layout={LoginSidebar}
          component={route.component}
          key={idx}
          exact
        />
      ))}

      {userRoutes.map((route, idx) => (
        <Authmiddleware
          path={route.path}
          layout={ResponsiveAppBar}
          component={route.component}
          key={idx}
          exact
        />
      ))}
    </Switch>
  );

  return (
    <React.Fragment>
      <Router>
        {/* 다른 곳에서 로그인 감지 시 강제 로그아웃 모달 (항상 마운트) */}
        <ForceLogoutDialog />
        {/* ✅ 항상 마운트하고, enabled로만 켜고 끈다 (로그인 전엔 비활성) */}
        <IdleTimerProvider
          enabled={isAuthed}
          idleMs={60 * 60 * 1000}  // 60분
          warnMs={60 * 1000}       // 1분 전 경고
          onWarn={handleWarn}
          onTimeout={handleTimeout}
          syncAcrossTabs={true}    // 🔥 일단 끄기
          resetOnTimeout={false}    // 🔥 이 탭에서만 처리, 자동 리셋 X
        >
          {AppRoutes}
        </IdleTimerProvider>
      </Router>
    </React.Fragment>
  );
};

export default App;
